import Page from '@app/components/layout/page/page';
import { useCCFUpsellDialog } from '@app/hooks/use-ccf-upsell-dialog';
import { useIntercom } from '@app/services/intercom/IntercomContext';
import Alert from '@shared/components/alert';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import TextLink from '@shared/components/content/text-link';
import { Accordion } from '@shared/components/ui/accordion';
import { Button } from '@shared/components/ui/button';
import { useCCF } from '../ccf-context';
import CCFAddScope3Tooltip from '../components/ccf-add-scope-3-tooltip';
import CCFHubSidebar from '../components/ccf-hub-sidebar';
import HubAccordionItem from '../components/hub-accordion-item';
import HubCard from '../components/hub-card';
import { useProcessScopes } from '../scopes';

export default function ReportHubPage() {
  const { showIntercomArticle } = useIntercom();
  const { scope1, scope2, scope3, secrCategories } = useProcessScopes();
  const { scope3Supported } = useCCF();
  const { showDialog, UpsellDialogComponent } = useCCFUpsellDialog();

  return (
    <Page name="Company carbon footprint hub" page="subpage">
      <div className="flex justify-between space-x-4 pb-6">
        <div className="w-full min-w-fit max-w-form grow">
          <Accordion
            type="multiple"
            defaultValue={['scope1', 'scope2', 'scope3']}
            className="w-full space-y-2"
          >
            <HubAccordionItem
              id="section-scope-1"
              title="Scope 1 - Direct emissions"
              value="scope1"
              testId="scope-1-accordion-trigger"
            >
              <Text variant="subtle" className="mb-2">
                The emissions from any process or activity within your company
                that directly produces greenhouse gases.{' '}
                <TextLink onClick={() => showIntercomArticle('scope1')}>
                  Learn more.
                </TextLink>
              </Text>

              {scope1.map((item) => {
                return (
                  <HubCard
                    key={item.slug}
                    slug={item.slug}
                    name={item.name}
                    totalEmissions={item.totalEmissions!}
                    description={item.description}
                    status={item.status}
                    href={item.href}
                    icon={item.icon}
                  />
                );
              })}
            </HubAccordionItem>
            <HubAccordionItem
              id="section-scope-2"
              title="Scope 2 - Indirect emissions from energy"
              value="scope2"
            >
              <Text variant="subtle" className="mb-2">
                The emissions from the energy your business purchases, generated
                by energy providers on your behalf.{' '}
                <TextLink onClick={() => showIntercomArticle('scope2')}>
                  Learn more.
                </TextLink>
              </Text>
              {scope2
                .filter(({ isSupported }) => !!isSupported)
                .map((item) => {
                  return (
                    <HubCard
                      key={item.slug}
                      slug={item.slug}
                      name={item.name}
                      totalEmissions={item.totalEmissions!}
                      description={item.description}
                      status={item.status}
                      href={item.href}
                      icon={item.icon}
                    />
                  );
                })}
            </HubAccordionItem>

            <HubAccordionItem
              id="section-scope-3"
              title="Scope 3 - Other indirect emissions"
              value="scope3"
              icon={
                !scope3Supported && (
                  <CCFAddScope3Tooltip
                    trigger={
                      <Icons.lock className="z-10 size-4 cursor-pointer" />
                    }
                    className="text-left"
                  />
                )
              }
            >
              <div className="mb-2 flex flex-col gap-y-4">
                <Text variant="subtle">
                  The emissions that occur across your supply chain, from the
                  production of the ingredients you purchase to waste disposal.{' '}
                  <TextLink onClick={() => showIntercomArticle('scope3')}>
                    Learn more.
                  </TextLink>
                </Text>
                {secrCategories.map((item) => (
                  <HubCard
                    key={item.slug}
                    slug={item.slug}
                    name={item.name}
                    scope3Category={item.scope3Category}
                    totalEmissions={item.totalEmissions!}
                    description={item.description}
                    status={item.status}
                    href={item.href}
                    icon={item.icon}
                  />
                ))}
                {!scope3Supported && (
                  <Alert
                    variant="info"
                    title="For food businesses, Scope 3 can account for ~90% of their total emissions"
                    className="mb-2 hidden h-min w-full space-y-2 border-none bg-teal-50 outline-none md:block"
                  >
                    <Text className="mb-2">
                      Your plan currently includes only Scope 1 and 2 reporting.
                      Book a call below to start including Scope 3 in your
                      report today.
                    </Text>
                    <Button
                      className="cursor-pointer bg-teal"
                      onClick={showDialog}
                    >
                      Get started
                    </Button>
                  </Alert>
                )}
              </div>
              {scope3
                .filter(({ included }) => included)
                .sort((a, b) => a.index! - b.index!)
                .map((item) => {
                  return (
                    <HubCard
                      key={item.slug}
                      slug={item.slug}
                      name={item.name}
                      scope3Category={item.scope3Category}
                      totalEmissions={
                        scope3Supported ? item.totalEmissions! : null
                      }
                      description={item.description}
                      status={item.status}
                      disabled={!scope3Supported || item.disabled}
                      href={item.href}
                      icon={item.icon}
                    />
                  );
                })}
            </HubAccordionItem>
            {scope3Supported && (
              <HubAccordionItem
                id="section-scope-3-additional"
                title="Scope 3 - Additional categories"
                value="scope3additional"
              >
                <div className="mb-2 flex flex-col gap-y-4">
                  <Text variant="subtle">
                    These categories are typically not required for food
                    companies, but you can add them to your report and include
                    total emission values calculated elsewhere.{' '}
                    <TextLink
                      onClick={() => showIntercomArticle('otherS3Categories')}
                    >
                      Learn more.
                    </TextLink>
                  </Text>
                  {!scope3Supported && (
                    <Alert
                      variant="info"
                      title="For food businesses, Scope 3 can account for ~90% of their total emissions"
                      className="mb-2 hidden h-min w-full space-y-2 border-none bg-teal-50 outline-none md:block"
                    >
                      <Text className="mb-2">
                        Your plan currently includes only Scope 1 and 2
                        reporting. Book a call below to start including Scope 3
                        in your report today.
                      </Text>
                      <Button
                        className="cursor-pointer bg-teal"
                        onClick={showDialog}
                      >
                        Get started
                      </Button>
                    </Alert>
                  )}
                </div>
                {scope3
                  .filter(({ included }) => !included)
                  .sort((a, b) => (a.index! < b.index! ? -1 : 1))
                  .map((item) => {
                    return (
                      <HubCard
                        key={item.slug}
                        slug={item.slug}
                        name={item.name}
                        scope3Category={item.scope3Category}
                        totalEmissions={
                          scope3Supported ? item.totalEmissions! : null
                        }
                        description={item.description}
                        status={item.status}
                        disabled={!scope3Supported || item.disabled}
                        href={item.href}
                        icon={item.icon}
                      />
                    );
                  })}
              </HubAccordionItem>
            )}
          </Accordion>
        </div>
        <CCFHubSidebar />
      </div>
      {UpsellDialogComponent}
    </Page>
  );
}
